import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase, CurrentUser, AuthError } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  currentUser: CurrentUser | null;
  loading: boolean;
  error: AuthError | null;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
  refreshUser: () => Promise<void>;
  
  // 権限チェック関数
  isSystemAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  hasPermission: (resource: string, action: string) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within SupabaseAuthProvider');
  }
  return context;
};

export const SupabaseAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);

  // APIクライアント設定
  const getApiUrl = () => {
    return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8766';
  };

  // ユーザー情報を取得
  const fetchCurrentUser = useCallback(async (accessToken: string) => {
    try {
      setError(null);
      // 管理画面専用エンドポイントを使用
      const response = await fetch(`${getApiUrl()}/api/auth/admin/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          // システム管理者でない場合
          await supabase.auth.signOut();
          throw new Error('この管理画面にアクセスするには、システム管理者権限が必要です。');
        }
        throw new Error(`Failed to fetch user info: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setCurrentUser(data.user);
      } else {
        throw new Error('Failed to load user information');
      }
    } catch (err: any) {
      console.error('Failed to fetch user info:', err);
      setError({ message: err.message || 'Failed to load user information' });
      setCurrentUser(null);
    }
  }, []);

  // 初期化
  useEffect(() => {
    let mounted = true;
    let isInitialized = false;

    const initAuth = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        
        if (mounted) {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          
          if (currentSession?.access_token) {
            await fetchCurrentUser(currentSession.access_token);
          }
          isInitialized = true;
        }
      } catch (err: any) {
        console.error('Auth initialization error:', err);
        if (mounted) {
          setError({ message: 'Failed to initialize authentication' });
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    // 認証状態の変更を監視
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('Auth state changed:', event, newSession?.user?.email);
        
        // 初期化後の変更のみ処理（重複を防ぐ）
        if (mounted && isInitialized) {
          setSession(newSession);
          setUser(newSession?.user ?? null);
          
          if (newSession?.access_token) {
            await fetchCurrentUser(newSession.access_token);
          } else {
            setCurrentUser(null);
          }
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchCurrentUser]);

  // ログイン
  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        throw new Error(signInError.message);
      }

      // onAuthStateChangeで自動的にユーザー情報が取得される
    } catch (err: any) {
      console.error('Sign in error:', err);
      setError({ message: err.message || 'ログインに失敗しました' });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Googleログイン
  const signInWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);
      
      const { data, error: signInError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
          scopes: 'email profile',
        },
      });

      if (signInError) {
        throw new Error(signInError.message);
      }
    } catch (err: any) {
      console.error('Google sign in error:', err);
      setError({ message: err.message || 'Googleログインに失敗しました' });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ログアウト
  const signOut = async () => {
    try {
      setError(null);
      const { error: signOutError } = await supabase.auth.signOut();
      
      if (signOutError) {
        throw new Error(signOutError.message);
      }
      
      // 状態をクリア
      setCurrentUser(null);
    } catch (err: any) {
      console.error('Sign out error:', err);
      setError({ message: err.message || 'ログアウトに失敗しました' });
    }
  };

  // エラークリア
  const clearError = () => {
    setError(null);
  };

  // ユーザー情報再取得
  const refreshUser = async () => {
    if (session?.access_token) {
      await fetchCurrentUser(session.access_token);
    }
  };

  // 権限チェック関数
  const isSystemAdmin = () => {
    return currentUser?.is_system_admin ?? false;
  };

  const isSuperAdmin = () => {
    return currentUser?.is_super_admin ?? false;
  };

  const hasPermission = (resource: string, action: string) => {
    if (!currentUser) return false;
    
    // スーパー管理者は全権限
    if (currentUser.is_super_admin) return true;
    
    const permission = `${resource}.${action}`;
    return currentUser.permissions.includes(permission);
  };

  const value: AuthContextType = {
    user,
    session,
    currentUser,
    loading,
    error,
    signIn,
    signInWithGoogle,
    signOut,
    clearError,
    refreshUser,
    isSystemAdmin,
    isSuperAdmin,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};